<template>
  <div class="mine-wrapper" ref="minePaneRef">
    <div class="mine-header">
      <div class="type-change-switch">
        <router-link to="/dashboard" class="dashboard-link">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.47917 0.8125C10.9749 0.8125 12.1875 2.02506 12.1875 3.52083V9.47917C12.1875 10.9749 10.9749 12.1875 9.47917 12.1875H3.52083C2.02506 12.1875 0.8125 10.9749 0.8125 9.47917V3.52083C0.8125 2.02506 2.02506 0.8125 3.52083 0.8125H9.47917ZM3.52083 1.89583C2.62337 1.89583 1.89583 2.62337 1.89583 3.52083V9.47917C1.89583 10.3766 2.62337 11.1042 3.52083 11.1042H4.875V1.89583H3.52083ZM5.95833 7.04167V11.1042H9.47917C10.3766 11.1042 11.1042 10.3766 11.1042 9.47917V7.04167H5.95833ZM5.95833 5.95833H11.1042V3.52083C11.1042 2.62337 10.3766 1.89583 9.47917 1.89583H5.95833V5.95833Z" fill="#EC0047"/>
          </svg>
          <p>Dashboard</p>
        </router-link>
        <div class="line"></div>
        <mine-type-selector
          :icon-class="['icon--card-list', 'icon--type-of-list' ]"
          :mode-type="['card', 'table']"
          :value="listType"
          @select="onChangeListType"
        />
      </div>
      <div class="history-report-count">{{ $t('mine.header.archivedReport') }}: {{ totalReportCnt }}</div>
      <div class="mine-search-wrap">
        <div v-if="searchedKeyword.length > 0" class="search-count">
          <em>{{ searchResultCnt }}</em>{{ $t('common.searchResult') }}
        </div>
        <div class="searchType-input">
          <button class="search-btn" @click="onSearch">
            <i class="icon icon--search size-16"></i>
          </button>
          <button class="clear-btn" v-show="keyword.length > 0" @click="onClearSearch">
            <i class="icon icon--close14"></i>
          </button>
          <input type="text"
                 ref="keyword"
                 :placeholder="$t('mine.header.search')"
                 v-model="keyword"
                 @keyup.enter="onSearch" />
        </div>
      </div>
    </div>
    <div v-if="isLoadingPageList" class="loading-wrapper">
      <ui-loading-circle>
        <span class="loading_notice" v-html="$t('common.loading')"></span>
      </ui-loading-circle>
    </div>
    <template v-else>
      <keep-alive v-if="flatReportList.length > 0">
        <mine-list-card-view
          v-if="listType === 'card'"
          :report-list="reportList"
          :dragging-item="draggingItem"
          :selected-report="selectedReport"
          :keyword="searchedKeyword"
          selected-tab="gpt"
          @retry="retryGetReport"
          @onDragStart="dragIn"
          @onDragEnd="dragOut"
          @remove="(report) => removeSelected(report)"
        />
        <MineListTableView
          v-else
          :report-list="flatReportList"
          :dragging-item="draggingItem"
          :selected-report="selectedReport"
          :keyword="searchedKeyword"
          selected-tab="gpt"
          @retry="retryGetReport"
          @onDragStart="dragIn"
          @onDragEnd="dragOut"
          @onSort="onChangeSortby"
          @remove="(report) => removeSelected(report)"
        />
      </keep-alive>
      <div v-else-if="searchedKeyword.length > 0" class="no-data">
        <i class="icon loading--empty"></i>
        <p class="no-data-title">{{ $t('common.noResult') }}</p>
        <p class="no-data-description">
          {{ $t('common.noResultSearch') }}
        </p>
        <p class="no-data-keyword">'{{ searchedKeyword }}'</p>
      </div>
    </template>
    <dimmed v-if="isChatOpened">
      <ChatContainer :report-list="selectedReport" @close="closeChat" />
    </dimmed>
  </div>
</template>
<script>
import MineTypeSelector from '@/components/mine/MineTypeSelector.vue'
import { EventBus } from '@/libs/event-bus'
import { getMineListData, retryReport } from '@/api/ai'
import { mapActions, mapMutations, mapState } from 'vuex'
import MineListCardView from '@/components/mine/MineListCardView.vue'
import MineListTableView from '@/components/mine/MineListTableView.vue'
import ChatContainer from '@/components/mine/chat/ChatContainer.vue'
import Dimmed from '@/components/ui/Dimmed.vue'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle.vue'
import { create } from 'vue-modal-dialogs'
import AppDialog from '@/components/ui/AppDialog.vue'

const openDialog = create(AppDialog, 'dialogType')
export default {
  name: 'Mine',
  components: { UiLoadingCircle, Dimmed, ChatContainer, MineListTableView, MineListCardView, MineTypeSelector },
  data () {
    return {
      listType: 'card',
      keyword: '',
      isComparing: false,
      searchedKeyword: '',
      searchResultCnt: 0,
      reportList: {},
      flatReportList: [],
      isLoadingPageList: false
    }
  },
  computed: {
    ...mapState([
      'draggingItem',
      'selectedReport',
      'isChatOpened',
      'totalReportCnt'
    ]),
    noData () {
      return this.totalReportCnt === 0
    }
  },
  methods: {
    ...mapMutations([
      'setSelectedReport',
      'clearSelectedReport',
      'setDraggingItem',
      'clearDraggingItem',
      'removeSelected',
      'setIsChatOpened'
    ]),
    retryGetReport (payload) {
      const requestData = {
        reportId: payload.id
      }
      retryReport(requestData)
        .then(res => {
          const { monthIdx, idx, flatIdx } = this.findMine(payload)
          this.reportList[monthIdx].list[idx].isError = false
          this.reportList[monthIdx].list[idx].isReportError = false
          this.reportList[monthIdx].list[idx].reportStatus = 'ing'
          this.flatReportList[flatIdx].isError = false
          this.flatReportList[flatIdx].isReportError = false
          this.flatReportList[flatIdx].reportStatus = 'ing'
          openDialog('report.reportRequest.success').then((response) => {
            if (response) {
              window.open('/mine', '_blank')
            }
          })
        })
        .catch(e => {
          console.log('에러')
          console.log(e)
        })
    },
    getMineList (searchText = null) {
      this.isLoadingPageList = true
      const requestData = {
        search: searchText,
        lang: this.$i18n.locale
      }
      getMineListData(requestData)
        .then(res => {
          const result = res.result
          if (!searchText) {
            this.searchedKeyword = ''
          } else {
            this.searchedKeyword = searchText
          }

          for (const list of Object.values(result.monthlyList)) {
            list.forEach(item => {
              if (!item.id) this.$set(item, 'id', item._id)
            })
          }

          this.reportList = Object.entries(result.monthlyList)
            .map(([month, list]) => ({
              month,
              list
            }))
            .sort((a, b) => b.month.localeCompare(a.month))

          const monthKeys = Object.keys(result.monthlyList).sort((a, b) => b.localeCompare(a))
          this.newFlatList = []

          for (let monthKey of monthKeys) {
            this.newFlatList.push(...result.monthlyList[monthKey])
          }

          this.flatReportList = this.newFlatList
          this.ReportListForSort = this.createListForSort(this.flatReportList)

          this.searchResultCnt = Object.values(result.monthlyList)
            .reduce((sum, arr) => sum + (arr.length || 0), 0)

          EventBus.$emit('onLoading', false)
        })
        .finally(() => {
          this.isLoadingPageList = false
        })
    },
    createListForSort (reportList) {
      let listForSort = {}
      reportList.forEach(report => {
        let subReport = report

        listForSort[report.id] = {
          regDate: report.regDate,
          title: report.title,
          type: subReport ? subReport.type : report.type,
          url: subReport ? subReport.url : report.url,
          period: subReport ? subReport.startDate + subReport.endDate : report.startDate + report.endDate,
          device: subReport ? subReport.device : report.device
        }
      })
      return listForSort
    },
    onChangeSortby (criteria) {
      this.flatReportList.sort((a, b) => {
        const aForSort = this.ReportListForSort[a.id][criteria.sortby]
        const bForSort = this.ReportListForSort[b.id][criteria.sortby]
        let valueA = aForSort ? typeof aForSort === 'string' ? aForSort : String(aForSort) : ''
        let valueB = bForSort ? typeof bForSort === 'string' ? bForSort : String(bForSort) : ''

        let result = criteria.direction > 0 ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
        if (result === 0) {
          const aRegDate = this.ReportListForSort[a.id].regDate
          const bRegDate = this.ReportListForSort[b.id].regDate
          result = bRegDate.localeCompare(aRegDate)
        }
        return result
      })
    },
    onChangeListType (type) {
      this.listType = type
      if (this.isComparing) {
        this.setReportComparing(false)
      }
    },
    onSearch () {
      if (this.isComparing) {
        this.setReportComparing(false)
      }
      this.getMineList(this.keyword)
    },
    onClearSearch () {
      this.keyword = ''
      if (this.searchedKeyword.length > 0) {
        this.getMineList()
      }
    },
    dragIn (report) {
      this.setDraggingItem(report)
    },
    dragOut () {
      this.clearDraggingItem()
      const ghostContainer = document.getElementById('mine-ghost')
      ghostContainer.classList.remove('visible')
    },
    closeChat () {
      this.setIsChatOpened(false)
      this.clearSelectedReport()
    }
  },
  created () {
    this.getMineList()
  }
}
</script>

<style scoped lang="scss">
.mine-wrapper {
  overflow: auto;
  height: calc(100vh - 54px);
  position: relative;

  .mine-header {
    padding: 20px 44px;
    position: relative;
    width: calc(100% - 88px);
    display: flex;
    align-items: center;
    gap: 16px;

    .type-change-switch {
      display: flex;
      align-items: center;
      gap: 20px;

      .dashboard-link {
        display: flex;
        align-items: center;
        gap: 4px;
        height: 32px;
        min-width: 80px;
        padding: 0 12px 0 10px;
        border-radius: 999px;
        border: 1px solid $brandColor;
        background-color: #fff;
        transition: all .3s;

        svg path {
          transition: all .3s;
        }

        p {
          color: $brandColor;
          text-align: center;
          font-size: 13px;
          font-weight: 600;
          line-height: 100%; /* 13px */
          transition: all .3s;
        }

        &:hover {
          background-color: $brandColor;

          svg path {
            fill: #fff;
          }

          p {
            color: #fff;
          }
        }
      }

      .line {
        width: 1px;
        height: 14px;
        background-color: #CCC;
      }
    }

    .history-report-count {
      flex-grow: 1;
      color: #666;
      font-size: 14px;
      font-weight: 500;
      line-height: 150%;
    }
    .mine-search-wrap {
      display: flex;
      align-items: center;
      gap: 16px;
      .search-count {
        font-weight: 400;
        color: #666;
        em {
          font-weight: 500;
          color: #F46200;
        }
      }
      .searchType-input {
        display: flex;
        align-items: center;
        position: relative;
        width: 200px;
        height: 32px;
        &.disabled input{
          background-color: #d7d7d7;
          opacity: 0.3;
          pointer-events: none;
        }
        input {
          width: 100%;
          padding: 9px 10px;
          border: solid 1px transparent;
          border-radius: 5px;
          background: #fff;
          font-size: 13px;
          color: #2F2F2F;
          box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 1px 2px 0px rgba(0, 0, 0, 0.20);
          box-sizing: border-box;
          &::placeholder {
            color: #BBB;
          }
          &:focus {
            border-color: #0096FF;
          }
        }
        button {
          display: flex;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          &.search-btn {
            right: 6px;
            padding: 2px;
          }
          &.clear-btn {
            right: 32px;
            padding: 3px;
          }
        }
      }
    }
  }

  .no-data {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    .no-data-title {
      font-size: 20px;
      font-weight: 600;
      color: #2f2f2f;
    }

    .no-data-description {
      font-size: 14px;
      line-height: 1.5;
      font-weight: 400;
      color: #767676;
    }

    .no-data-keyword {
      font-size: 16px;
      font-weight: 600;
      line-height: 1.5;
      color: #ff8400;
    }
  }

  .loading-wrapper {
    position: relative;
    width: calc(100% - 210px);
    height: calc(100% - 158px);
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
